import React, { useState } from 'react';
import Icon from 'components/AppIcon';

export default function AutoTrailerGenerator({ game, onClose }) {
  const [stage, setStage] = useState('idle'); // idle | generating | done
  const [progress, setProgress] = useState(0);
  const [copied, setCopied] = useState(false);

  const STAGES = [
    'Analyzing your creation...',
    'Extracting key moments...',
    'Generating trailer cuts...',
    'Adding music & effects...',
    'Rendering final trailer...',
  ];
  const [stageIndex, setStageIndex] = useState(0);

  const handleGenerate = async () => {
    setStage('generating');
    setProgress(0);
    setStageIndex(0);

    for (let i = 0; i < STAGES?.length; i++) {
      setStageIndex(i);
      for (let p = (i / STAGES?.length) * 100; p < ((i + 1) / STAGES?.length) * 100; p += 5) {
        await new Promise(r => setTimeout(r, 120));
        setProgress(p);
      }
    }
    setProgress(100);
    setStage('done');
  };

  const handleCopyLink = async () => {
    const link = `${window.location?.origin}/trailer/${game?.id}`;
    try { await navigator.clipboard?.writeText(link); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!game) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => e?.target === e?.currentTarget && onClose?.()}
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden animate-bounce-in"
        style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-xl)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)', background: 'rgba(99,102,241,0.06)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-primary)' }}>
              <Icon name="Clapperboard" size={16} color="white" />
            </div>
            <div>
              <h3 className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Auto Trailer Generator</h3>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{game?.title}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-4">
          {stage === 'idle' && (
            <>
              <div className="rounded-xl p-4 text-center" style={{ background: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(99,102,241,0.12)' }}>
                  <Icon name="Clapperboard" size={24} color="var(--color-primary)" />
                </div>
                <p className="text-sm font-semibold mb-1" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Generate a Promo Trailer</p>
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                  LoopNest AI will automatically create a short promotional trailer for "{game?.title}" that you can share on social media.
                </p>
              </div>
              <button
                onClick={handleGenerate}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: 'linear-gradient(135deg, var(--color-primary), var(--color-secondary))', color: 'white', fontFamily: 'var(--font-heading)' }}
              >
                <Icon name="Wand2" size={16} color="white" />
                Generate Trailer
              </button>
            </>
          )}

          {stage === 'generating' && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)' }}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-primary)' }}>
                  <Icon name="Cpu" size={16} color="white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Creating your trailer...</p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{STAGES?.[stageIndex]}</p>
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs mb-1.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>
                  <span>Progress</span>
                  <span>{Math.round(progress)}%</span>
                </div>
                <div className="h-2.5 rounded-full overflow-hidden" style={{ background: 'var(--color-muted)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${progress}%`, background: 'linear-gradient(90deg, var(--color-primary), var(--color-secondary))' }}
                  />
                </div>
              </div>
            </div>
          )}

          {stage === 'done' && (
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2 p-3 rounded-xl" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
                <Icon name="CheckCircle2" size={16} color="var(--color-success)" />
                <p className="text-xs font-semibold" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-heading)' }}>Trailer generated successfully!</p>
              </div>
              <div className="rounded-xl overflow-hidden relative h-36" style={{ background: '#0a0a0f' }}>
                <img src={game?.thumbnail} alt={`Trailer preview for ${game?.title}`} className="w-full h-full object-cover opacity-70" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
                    <Icon name="Play" size={20} color="#1a1a1a" />
                  </div>
                </div>
                <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
                  <span className="text-xs text-white font-semibold" style={{ fontFamily: 'var(--font-caption)', textShadow: '0 1px 4px rgba(0,0,0,0.8)' }}>0:15 trailer</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(99,102,241,0.9)', color: 'white', fontFamily: 'var(--font-caption)' }}>HD</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleCopyLink}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                  style={{ background: copied ? 'rgba(16,185,129,0.1)' : 'rgba(99,102,241,0.1)', color: copied ? 'var(--color-success)' : 'var(--color-primary)', border: `1px solid ${copied ? 'var(--color-success)' : 'var(--color-primary)'}`, fontFamily: 'var(--font-heading)' }}
                >
                  <Icon name={copied ? 'Check' : 'Link'} size={13} />
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
                <button
                  onClick={() => {
                    const platforms = ['YouTube', 'TikTok', 'Instagram'];
                    alert(`Share to: ${platforms?.join(', ')}`);
                  }}
                  className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                  style={{ background: 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}
                >
                  <Icon name="Share2" size={13} color="white" />
                  Share Trailer
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
